// src/lib/menu/generateMenuUrls.ts

import { href, slugFor, type Locale } from '@/lib/localized-paths';
import { ItemId, SectionId, MenuId } from './enum';

/* =========================
 * Types: base (tanpa URL)
 * =======================*/
export type MenuItem = {
  id: ItemId;
  hasUrl?: boolean;
};

export type Section = {
  id: SectionId;
  items?: MenuItem[];
  hasUrl?: boolean;
};

export type MegaMenuConfig = Partial<Record<MenuId, Section[]>>;

/* =========================
 * Types: hasil (dengan URL)
 * =======================*/
export type MenuItemWithUrl = MenuItem & { url: string };

export type SectionWithUrl =
  | { id: SectionId; items: MenuItemWithUrl[] }       // Section punya items → URL di item
  | { id: SectionId; url: string; items?: undefined } // Section tanpa items → URL di section

export type MegaMenuResolved = Record<MenuId, SectionWithUrl[]>;

function omitUrlFlag<T extends { hasUrl?: boolean }>(obj: T): Omit<T, 'hasUrl'> {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { hasUrl, ...rest } = obj;
  return rest;
}
/* ==========================================================
 * Generator: isi URL otomatis berdasar enum + locale aktif
 * ========================================================*/
export function withLocalizedUrls(locale: Locale, cfg: MegaMenuConfig): MegaMenuResolved {
  const out = {} as MegaMenuResolved;

  (Object.keys(cfg) as MenuId[]).forEach((menuId) => {
    if (!cfg[menuId]) return;
    out[menuId] = cfg[menuId].map((section) => {
      const hasItems = Array.isArray(section.items) && section.items.length > 0;

      if (hasItems) {
        const items: MenuItemWithUrl[] = section.items!.map((it) => ({
          ...omitUrlFlag(it),
          url: href(locale, menuId, section.id, it.id),
        }));
        return { id: section.id, items };
      }

      // Section tanpa items: hanya buat link bila flag url !== false (default true)
      // Kalau kamu ingin strict flag, ganti jadi: if (section.url) ...
      const makeLink = section.hasUrl ?? true;
      return makeLink
        ? { id: section.id, url: href(locale, menuId, section.id) }
        : ({ id: section.id } as SectionWithUrl);
    });
  });

  return out;
}

/* ==========================================================
 * Helper opsional: buat path parsial dari segmen bebas + enum
 * ========================================================*/
export function joinLocalized(
  locale: Locale,
  ...segments: Array<string | MenuId | SectionId | ItemId>
) {
  const slugs = segments.map((s) => (typeof s === 'string' ? s : slugFor(locale, s)));
  return '/' + [locale, ...slugs.map((x) => x.replace(/^\/+|\/+$/g, ''))].join('/');
}

/* =====================
 * MENU_CONFIG (manual)
 * ====================*/
export const MENU_CONFIG_BASE: MegaMenuConfig = {
  [MenuId.AboutUs]: [
    { id: SectionId.CompanyProfile, hasUrl: true },
    {
      id: SectionId.Management,
      items: [
        { id: ItemId.Commissioners, hasUrl: true },
        { id: ItemId.Directors, hasUrl: true },
        { id: ItemId.ShariaSupervisoryBoard, hasUrl: true },
      ],
    },
    { id: SectionId.Committees, hasUrl: true },
    { id: SectionId.CorporateActions, hasUrl: true },
    { id: SectionId.Certifications, hasUrl: true },
    {
      id: SectionId.Governance,
      items: [
        { id: ItemId.Policies, hasUrl: true },
      ],
    },
    { id: SectionId.CorporateSecretary, hasUrl: true },
    { id: SectionId.Affiliations, hasUrl: true },
  ],

  [MenuId.Services]: [
    { id: SectionId.Securitization, hasUrl: true },
    { id: SectionId.RetailEba, hasUrl: true },
    { id: SectionId.ShariaUnit, hasUrl: true },
    { id: SectionId.Financing, hasUrl: true },
    { id: SectionId.Bonds, hasUrl: true },
    { id: SectionId.MortgageStandardization, hasUrl: true },
    {
      id: SectionId.Training,
      items: [
        { id: ItemId.TrainingSchedule, hasUrl: true },
      ],
    },
  ],

  [MenuId.Publications]: [
    { id: SectionId.Ecosystem, hasUrl: true },
    { id: SectionId.NewsActivities, hasUrl: true },
    { id: SectionId.Biweekly, hasUrl: true },
    { id: SectionId.Emagazine, hasUrl: true },
    {
      id: SectionId.ProcurementInformation,
      items: [
        { id: ItemId.ListOfPlannedProcurement, hasUrl: true },
        { id: ItemId.TenderInformation, hasUrl: true },
      ],
    },
    { id: SectionId.GeneralNotification, hasUrl: true },
  ],

  [MenuId.InvestorRelations]: [
    {
      id: SectionId.FinancialReports,
      items: [
        { id: ItemId.AnnualFinancialReport, hasUrl: true },
        { id: ItemId.InterimFinancialReport, hasUrl: true },
      ],
    },
    { id: SectionId.AnnualReports, hasUrl: true },
    { id: SectionId.EbaSpPerformance, hasUrl: true },
    { id: SectionId.SocialBonds, hasUrl: true },
    {
      id: SectionId.InvestmentProducts,
      items: [
        { id: ItemId.AssetInvestments, hasUrl: true },
        { id: ItemId.BondInvestments, hasUrl: true },
        { id: ItemId.SocialBondInvestments, hasUrl: true },
      ],
    },
    { id: SectionId.DividendInfo, hasUrl: true },
    {
      id: SectionId.ImportantInfo,
      items: [
        { id: ItemId.InvestorUpdate, hasUrl: true },
        { id: ItemId.PublicAnnouncement, hasUrl: true },
      ],
    },
    { id: SectionId.CorporateRatings, hasUrl: true },
  ],

  [MenuId.Sustainability]: [
    { id: SectionId.CorporateSocialResponsibilities, hasUrl: true },
    { id: SectionId.SustainabilityReport, hasUrl: true },
  ],
};
